import { queryClient } from 'App';
import { getRecoil, setRecoil } from 'recoil-nexus';

import { iaRa, toNumericId } from 'lib/helpers/utility';

import { runKey } from 'components/admin/pages/Imports/ImportRequests/lib/constants';
import { isTerminal } from 'components/admin/pages/Imports/ImportRequests/components/ProcessImportSteps/lib/constants';

import { importProgressState } from './importProgressState';

const sameRun = (a, b) => a?.kind === b?.kind && toNumericId(a?.id) === toNumericId(b?.id);

// Newest first: ImportStep and the run lists read `runs[0]` as the latest.
const byNewest = (a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0);

// `at` is the emitter's clock. Ticks for one run can overtake each other, so a
// payload older than what the run already carries is dropped rather than merged
// — otherwise a late `processing` would overwrite `finished`.
const upsertRun = (runs, run, at) => {
  const list = iaRa(runs);
  const index = list.findIndex((item) => sameRun(item, run));

  if (index === -1) return [...list, { ...run, _at: at }].sort(byNewest);

  const existing = list[index];
  if (at && existing._at && at < existing._at) return list;

  const next = [...list];
  // `id` stays as the fetched row had it: screens compare it against numeric
  // columns (final_import_info_id), so a stringified id off the wire would break
  // those comparisons.
  next[index] = { ...existing, ...run, id: existing.id, _at: at || existing._at };
  return next.sort(byNewest);
};

const bumpCount = (count, delta) => {
  const value = Number(count);
  return Number.isFinite(value) ? Math.max(value + delta, 0) : count;
};

const patchDetail = ({ requestId, request, run, at }) => {
  queryClient.setQueriesData(['import-request'], (oldData) => {
    if (!oldData?.request || toNumericId(oldData.request.id) !== requestId) return oldData;

    const merged = { ...oldData.request, ...(request || {}) };
    if (run) merged.imports = upsertRun(merged.imports, run, at);

    return { ...oldData, request: merged };
  });
};

const patchList = ({ requestId, request, created, removed }) => {
  queryClient.setQueriesData(['import-requests'], (oldData) => {
    if (!oldData) return oldData;

    const rows = iaRa(oldData.requests);
    const index = rows.findIndex((row) => toNumericId(row?.id) === requestId);
    const hasCount = oldData.count !== undefined && oldData.count !== null;

    if (removed) {
      if (index === -1) return oldData;
      return {
        ...oldData,
        requests: rows.filter((_row, position) => position !== index),
        ...(hasCount && { count: bumpCount(oldData.count, -1) })
      };
    }

    if (index === -1) {
      if (!created || !request) return oldData;
      return {
        ...oldData,
        requests: [request, ...rows],
        ...(hasCount && { count: bumpCount(oldData.count, 1) })
      };
    }

    if (!request) return oldData;

    const next = [...rows];
    next[index] = { ...next[index], ...request };
    return { ...oldData, requests: next };
  });
};

// A run that will not tick again has no live progress to show; leaving the entry
// behind would keep a stale bar under the next run with the same id.
const clearProgress = (run) => {
  if (run?.id === undefined || run?.id === null) return;

  const key = runKey(run);
  const prev = getRecoil(importProgressState);
  if (!(key in prev)) return;

  const { [key]: _finished, ...rest } = prev;
  setRecoil(importProgressState, rest);
};

/**
 * kind 'import-request-update' — the admin Import Requests screens are kept
 * current by this alone: the detail query and the grid rows are patched in
 * place, never invalidated. A payload for a request nobody has cached is
 * dropped, since the initial load will fetch it.
 */
export default (data) => {
  const { importRequestId, request, run, created, removed, at } = data || {};
  const requestId = toNumericId(importRequestId);
  if (requestId === undefined || requestId === null || requestId === '') return;

  if (!removed && (request || run)) patchDetail({ requestId, request, run, at });

  patchList({ requestId, request, created, removed });

  if (run && isTerminal(run.status)) clearProgress(run);
};
